import React from 'react';

import { withIntl, IntlProps } from 'ha/i18n';
import * as SearchFilters from 'ha/types/SearchFilters';
import Box from 'ha/ui/next/Box';
import Divider from 'ha/ui/next/Divider';

import FilterPopover, { WidthType, ButtonElementType } from './FilterPopover';
import FiltersTags from './FiltersTags';
import TypeFilter from './TypeFilter';
import FurnitureFilter from './FurnitureFilter';
import BillsFilter from './BillsFilter';
import RegistrationFilter from './RegistrationFilter';
import RecentlyAddedFilter from './RecentlyAddedFilter';
import RulesFilter from './RulesFilter';
import GenderFilter from './GenderFilter';
import SuitableForFilter from './SuitableForFilter';

import { RegistrationCountryCodes } from 'ha/constants/Filters';

export interface Props {
  searchParams: {};
  onChangeFilters: SearchFilters.FilterSubmit;
  loadPreviewNumberOfListings: (searchParams: {}) => void;
  clearPreviewNumberOfListings: () => void;
  typeFilter: SearchFilters.TypeFilterValue;
  furnitureFilter: SearchFilters.FurnitureFilterValue;
  billsFilter: SearchFilters.BillsFilterValue;
  roomsFilter: SearchFilters.RoomsFilterValue;
  registrationFilter: SearchFilters.RegistrationFilterValue;
  recentlyAddedFilter: SearchFilters.RecentlyAddedFilterValue;
  rulesFilter: SearchFilters.RulesFilterValue;
  genderFilter: SearchFilters.GenderFilterValue;
  suitableForFilter: SearchFilters.SuitableForFilterValue;
  countryCode: string;
  button: ButtonElementType;
  toggleFilterPopover?: (open: boolean) => void;
  updateButtonText: string;
}

interface State {
  types: SearchFilters.TypeFilterValue;
  furniture: SearchFilters.FurnitureFilterValue;
  bills: SearchFilters.BillsFilterValue;
  rooms: SearchFilters.RoomsFilterValue;
  registration: SearchFilters.RegistrationFilterValue;
  recentlyAdded: SearchFilters.RecentlyAddedFilterValue;
  rules: SearchFilters.RulesFilterValue;
  gender: SearchFilters.GenderFilterValue;
  suitableFor: SearchFilters.SuitableForFilterValue;
}

type FilterValues = Partial<State>;

const defaultFilters = {
  types: [],
  rules: [],
  gender: [],
  bills: null,
  furniture: null,
  registration: null,
  recentlyAdded: null,
  rooms: {
    bedroomCount: [],
  },
  suitableFor: [],
};

class OtherFilterDesktop extends React.Component<Props & IntlProps, State> {
  state: State;

  constructor(props: Props & IntlProps) {
    super(props);

    this.state = this.getStateFromProps();
  }

  getStateFromProps = () => {
    const types = this.props.typeFilter.filter(Boolean);

    return {
      types,
      furniture: this.props.furnitureFilter,
      bills: this.props.billsFilter,
      rooms: this.props.roomsFilter,
      registration: this.props.registrationFilter,
      recentlyAdded: this.props.recentlyAddedFilter,
      rules: this.props.rulesFilter.filter(Boolean),
      gender: this.props.genderFilter,
      suitableFor: this.props.suitableForFilter,
    };
  };

  handleOpen = () => {
    this.setState(this.getStateFromProps());
  };

  handleClose = () => {
    this.props.clearPreviewNumberOfListings();
  };

  handleUpdate = () => {
    const { onChangeFilters, intl } = this.props;

    onChangeFilters({ ...this.state, intl });
  };

  updateFilters = (values: FilterValues) => {
    this.setState(
      state => ({ ...state, ...values }),
      () => {
        const filteredSearchParams = {
          ...this.props.searchParams,
          ...this.state,
          categories: this.state.types,
        };

        this.props.loadPreviewNumberOfListings(filteredSearchParams);
      },
    );
  };

  handleReset = () => {
    this.setState(defaultFilters);
  };

  handleTypeFilterToggle = (
    type: SearchFilters.TypeValue,
    selected: boolean,
  ) => {
    const types = selected
      ? this.state.types.concat(type)
      : this.state.types.filter(current => type !== current);

    const rooms = types.includes(SearchFilters.TypeValue.APARTMENT)
      ? this.state.rooms
      : {
          bedroomCount: [],
        };

    this.updateFilters({ types, rooms });
  };

  handleRoomsFilterChange = (
    type: SearchFilters.RoomsValue,
    selected: boolean,
  ) => {
    const bedroomCount = selected
      ? this.state.rooms.bedroomCount.concat(type)
      : this.state.rooms.bedroomCount.filter(current => type !== current);

    const types = [...this.state.types];
    if (!types.includes(SearchFilters.TypeValue.APARTMENT)) {
      types.push(SearchFilters.TypeValue.APARTMENT);
    }

    this.updateFilters({
      types,
      rooms: {
        bedroomCount,
      },
    });
  };

  handleFurnitureFilterChange = (furniture: SearchFilters.FurnitureValue) => {
    this.updateFilters({ furniture });
  };

  handleBillsFilterChange = (bills: SearchFilters.BillsValue) => {
    this.updateFilters({ bills });
  };

  handleRegistrationFilterChange = (
    registration: SearchFilters.RegistrationValue,
  ) => {
    this.updateFilters({ registration });
  };

  handleRulesFilterToggle = (
    selection: SearchFilters.RulesValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({ rules: this.state.rules.concat(selection) });
    } else {
      this.updateFilters({
        rules: this.state.rules.filter(current => selection !== current),
      });
    }
  };

  handleGenderFilterToggle = (
    selection: SearchFilters.GenderValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({ gender: this.state.gender.concat(selection) });
    } else {
      this.updateFilters({
        gender: this.state.gender.filter(current => selection !== current),
      });
    }
  };

  handleRecentlyAddedFilterChange = (
    value: SearchFilters.RecentlyAddedFilterValue,
  ) => {
    this.updateFilters({
      recentlyAdded: value,
    });
  };

  handleSuitableForFilterToggle = (
    selection: SearchFilters.SuitableForValue,
    selected: boolean,
  ) => {
    if (selected) {
      this.updateFilters({
        suitableFor: this.state.suitableFor.concat(selection),
      });
    } else {
      this.updateFilters({
        suitableFor: this.state.suitableFor.filter(
          current => selection !== current,
        ),
      });
    }
  };

  handleFilterRemove = (filter: string, value?: string) => {
    if (filter === 'type') {
      const types = this.state.types.filter(current => value !== current);
      const rooms =
        value === SearchFilters.TypeValue.APARTMENT
          ? {
              bedroomCount: [],
            }
          : this.state.rooms;

      this.updateFilters({
        types,
        rooms,
      });
    } else if (filter === 'bedroomCount') {
      this.updateFilters({
        rooms: {
          bedroomCount: this.state.rooms.bedroomCount.filter(
            current => value !== current,
          ),
        },
      });
    } else if (filter === 'rules') {
      this.updateFilters({
        rules: this.state.rules.filter(current => value !== current),
      });
    } else if (filter === 'gender') {
      this.updateFilters({
        gender: this.state.gender.filter(current => value !== current),
      });
    } else if (filter === 'suitableFor') {
      this.updateFilters({
        suitableFor: this.state.suitableFor.filter(
          current => value !== current,
        ),
      });
    } else if (filter === 'furniture') {
      this.updateFilters({ furniture: null });
    } else if (filter === 'bills') {
      this.updateFilters({ bills: null });
    } else if (filter === 'registration') {
      this.updateFilters({ registration: null });
    } else if (filter === 'recentlyAdded') {
      this.updateFilters({ recentlyAdded: null });
    }
  };

  render() {
    const {
      countryCode,
      intl: { T },
      button,
      toggleFilterPopover,
      updateButtonText,
    } = this.props;
    const {
      types,
      furniture,
      bills,
      rooms,
      registration,
      recentlyAdded,
      rules,
      gender,
      suitableFor,
    } = this.state;

    const filterCount =
      types.length +
      gender.length +
      (furniture ? 1 : 0) +
      (bills ? 1 : 0) +
      (registration ? 1 : 0) +
      rules.length +
      suitableFor.length +
      (recentlyAdded ? 1 : 0) +
      rooms.bedroomCount.length;

    const showFiltersTags = filterCount > 0;
    const tags = showFiltersTags ? (
      <React.Fragment>
        <Box paddingRight={4}>
          <FiltersTags
            types={types}
            furniture={furniture}
            bills={bills}
            suitableFor={suitableFor}
            registration={registration}
            recentlyAdded={recentlyAdded}
            gender={gender}
            rooms={rooms}
            rules={rules}
            onRemove={this.handleFilterRemove}
          />
        </Box>
        <Box paddingTop={2}>
          <Divider />
        </Box>
      </React.Fragment>
    ) : null;

    return (
      <FilterPopover
        button={button}
        updateButtonText={updateButtonText}
        resetButtonText={T('Reset filters')}
        onUpdateButtonClick={this.handleUpdate}
        onResetButtonClick={this.handleReset}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        toggleFilterPopover={toggleFilterPopover}
        width={WidthType.lg}
        tags={tags}
        data-test-locator="Filters OtherFilterDesktop FilterPopover"
      >
        <TypeFilter
          types={types}
          onToggle={this.handleTypeFilterToggle}
          rooms={rooms}
          onRoomsFilterChange={this.handleRoomsFilterChange}
          data-test-locator="Filters OtherFilterDesktop TypeFilter"
        />

        <hr />

        <FurnitureFilter
          furniture={furniture}
          onChange={this.handleFurnitureFilterChange}
          data-test-locator="Filters OtherFilterDesktop FurnitureFilter"
        />

        <hr />

        <BillsFilter
          bills={bills}
          onChange={this.handleBillsFilterChange}
          data-test-locator="Filters OtherFilterDesktop BillsFilter"
        />

        <hr />

        <SuitableForFilter
          suitableFor={suitableFor}
          onToggle={this.handleSuitableForFilterToggle}
          data-test-locator="Filters OtherFilterDesktop SuitableForFilter"
        />

        <hr />

        <GenderFilter
          gender={gender}
          onToggle={this.handleGenderFilterToggle}
          data-test-locator="Filters OtherFilterDesktop GenderFilter"
        />

        <hr />

        <RulesFilter
          rules={rules}
          onToggle={this.handleRulesFilterToggle}
          data-test-locator="Filters OtherFilterDesktop RulesFilter"
        />

        {RegistrationCountryCodes.includes(countryCode) && (
          <React.Fragment>
            <hr />

            <RegistrationFilter
              registration={registration}
              onChange={this.handleRegistrationFilterChange}
              data-test-locator="Filters OtherFilterDesktop RegistrationFilter"
            />
          </React.Fragment>
        )}

        <hr />

        <RecentlyAddedFilter
          value={recentlyAdded}
          onChange={this.handleRecentlyAddedFilterChange}
          data-test-locator="Filters OtherFilterDesktop RecentlyAddedFilter"
        />
      </FilterPopover>
    );
  }
}

export default withIntl(OtherFilterDesktop);
